import { __spreadArray } from "tslib";
import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
var packetId = '__fromUseIframeHook';
function encodeMessage(message, fromId) {
    var _a;
    return JSON.stringify((_a = {
            fromId: fromId,
            payload: JSON.stringify(message)
        },
        _a[packetId] = true,
        _a));
}
function decodeMessage(raw) {
    try {
        var decoded = JSON.parse(raw);
        if (packetId in decoded && decoded[packetId] === true) {
            var message = JSON.parse(decoded.payload);
            return [message, decoded.fromId];
        }
    }
    catch (e) {
        return undefined;
    }
    return undefined;
}
function isPrivateMessage(message) {
    return (Object.prototype.hasOwnProperty.call(message, '__private') && '__private' in message && message.__private === true);
}
export function useIframeMessage(handleMessage, options) {
    var ref = options === null || options === void 0 ? void 0 : options.ref;
    var isParent = useMemo(function () { return ref !== undefined; }, [ref]);
    var isChild = !isParent && window !== window.top;
    /** Scope: Parent */
    var _a = useState(), iframeId = _a[0], setIframeId = _a[1];
    useEffect(function () {
        var _a;
        setIframeId((_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.id);
    }, [ref]);
    var _b = useState([]), queue = _b[0], setQueue = _b[1];
    var _c = useState(), childId = _c[0], setChildId = _c[1];
    /** Scope Parent */
    var isChildMounted = childId !== undefined;
    var dispatch = useCallback(function (message) {
        if (isParent || isChild) {
            setQueue(function (q) { return __spreadArray(__spreadArray([], q, true), [message], false); });
        }
    }, [isParent, isChild]);
    var dispatchPrivate = useCallback(function (message) {
        if (isParent || isChild) {
            setQueue(function (q) { return __spreadArray(__spreadArray([], q, true), [message], false); });
        }
    }, [isParent, isChild]);
    var handlePrivateMessage = useCallback(function (message) {
        switch (message.type) {
            case 'mounted':
                if (message.id === iframeId) {
                    return setChildId(message.id);
                }
        }
    }, [iframeId]);
    var postMessage = useCallback(function (message) {
        var _a;
        if (isParent && (ref === null || ref === void 0 ? void 0 : ref.current)) {
            (_a = ref.current.contentWindow) === null || _a === void 0 ? void 0 : _a.postMessage(encodeMessage(message), '*');
        }
        else if (isParent === false) {
            window.parent.postMessage(encodeMessage(message, childId), '*');
        }
    }, [isParent, childId, ref]);
    useEffect(function () {
        var message = queue[0];
        if (isParent) {
            if (message && isChildMounted) {
                setQueue(function (q) { return q.slice(1); });
                postMessage(message);
            }
        }
        else {
            if (message) {
                setQueue(function (q) { return q.slice(1); });
                postMessage(message);
            }
        }
    }, [isParent, isChildMounted, postMessage, queue]);
    var eventListener = useCallback(function (event) {
        var _a = decodeMessage(event.data) || [], message = _a[0], fromId = _a[1];
        if (message) {
            if (isPrivateMessage(message)) {
                handlePrivateMessage(message);
            }
            else {
                if (isParent) {
                    if (iframeId === fromId) {
                        handleMessage(message, dispatch);
                    }
                }
                else {
                    handleMessage(message, dispatch);
                }
            }
        }
    }, [handlePrivateMessage, isParent, iframeId, handleMessage, dispatch]);
    useEventListener(eventListener);
    useEffect(function () {
        if (!isParent) {
            var urlParams = new URLSearchParams(window.location.search);
            var id = urlParams.get('id') || '';
            dispatchPrivate({ type: 'mounted', id: id, __private: true });
            setChildId(id);
        }
    }, [dispatchPrivate, isParent]);
    if (isParent && (ref === null || ref === void 0 ? void 0 : ref.current) && !iframeId) {
        throw new Error('iframes must have an ID set');
    }
    return [dispatch];
}
export function useIframeEvent(eventName, handleEvent, options) {
    var handleMessage = useCallback(function (event) {
        if (handleEvent && event === eventName) {
            handleEvent();
        }
    }, [handleEvent, eventName]);
    var dispatch = useIframeMessage(handleMessage, options)[0];
    var onEventHandler = useCallback(function () {
        dispatch(eventName);
    }, [eventName, dispatch]);
    return [onEventHandler];
}
export function useIframeSharedState(initialState, options) {
    var stateAge = useRef({
        local: new Date().getTime(),
        remote: 0,
    });
    var _a = useState(initialState), internalState = _a[0], setInternalState = _a[1];
    var _b = useState(initialState), remoteState = _b[0], setRemoteState = _b[1];
    var handler = useCallback(function (message) {
        switch (message.type) {
            case '__private_set-state':
                stateAge.current.remote = message.time;
                return setRemoteState(message.state);
        }
    }, []);
    var dispatch = useIframeMessage(handler, options)[0];
    useEffect(function () {
        var now = new Date().getTime();
        stateAge.current.local = now;
        dispatch({ type: '__private_set-state', state: internalState, time: now });
    }, [internalState, dispatch]);
    var state = stateAge.current.local > stateAge.current.remote ? internalState : remoteState;
    return [state, setInternalState];
}
function useEventListener(handler, el) {
    var savedHandler = useRef();
    useEffect(function () {
        savedHandler.current = handler;
    }, [handler]);
    useEffect(function () {
        var element = el || window;
        var isSupported = element && element.addEventListener;
        if (!isSupported)
            return;
        var eventListener = function (event) { var _a; return (_a = savedHandler.current) === null || _a === void 0 ? void 0 : _a.call(savedHandler, event); };
        element.addEventListener('message', eventListener);
        return function () {
            element.removeEventListener('message', eventListener);
        };
    }, [el]);
}
//# sourceMappingURL=useIframeMessage.js.map